import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, ArrowLeft, Sparkles, Bookmark, RotateCcw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import type { AnalysisResult } from '../lib/supabase'

function SuccessPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const assessmentId = searchParams.get('assessment_id')
  const sessionId = searchParams.get('session_id')

  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [, setVerified] = useState(false)

  useEffect(() => {
    if (!assessmentId) {
      navigate('/')
      return
    }

    // Try localStorage first
    const cached = localStorage.getItem(`stresslens_result_${assessmentId}`)
    if (cached) {
      try {
        setResult(JSON.parse(cached))
      } catch { /* fall through */ }
    }

    // Verify payment if session_id exists
    if (sessionId) {
      fetch(`/api/verify-session?session_id=${sessionId}&assessment_id=${assessmentId}`)
        .then(r => r.json())
        .then(data => {
          setVerified(data.paid)
        })
        .catch(() => setVerified(false))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [assessmentId, sessionId, navigate])

  const stressConfig = {
    mild: { label: 'Mild', color: 'bg-mild/10 text-mild border-mild/30', icon: CheckCircle2 },
    moderate: { label: 'Moderate', color: 'bg-moderate/10 text-moderate border-moderate/30', icon: AlertTriangle },
    severe: { label: 'Severe', color: 'bg-severe/10 text-severe border-severe/30', icon: AlertTriangle }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot" />
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot-delay-1" />
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot-delay-2" />
          </div>
          <p className="text-text-light text-sm">Verifying your purchase...</p>
        </div>
      </div>
    )
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-severe mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text mb-2">Report not found</h2>
          <p className="text-text-light text-sm mb-6">We couldn't find your assessment. Please try again.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-dark transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  const stress = stressConfig[result.stress_level]
  const StressIcon = stress.icon

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <nav className="w-full bg-bg/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-text-light hover:text-text transition-colors">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="font-semibold text-text text-sm">StressLens</span>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-16">
        {/* Success Banner */}
        <div className="bg-mild/10 border border-mild/30 rounded-xl p-4 mb-6 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-mild shrink-0" />
          <div>
            <h2 className="font-semibold text-text">Your report is unlocked</h2>
            <p className="text-sm text-text-light">You now have full access to your complete assessment.</p>
          </div>
        </div>

        {/* Crisis Banner */}
        {result.risk_level === 'high' && (
          <div className="bg-severe/10 border border-severe/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-severe shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-severe text-sm mb-1">We're concerned about you</h3>
                <p className="text-sm text-text-light mb-3">Your responses suggest you may be going through a very difficult time. Please reach out for support right now.</p>
                <div className="space-y-1.5">
                  {result.seek_help.crisis_resources.map((r, i) => (
                    <div key={i} className="text-sm font-medium text-severe">{r}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stress Level Badge */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold ${stress.color}`}>
            <StressIcon className="w-4 h-4" />
            Your stress level: {stress.label}
          </div>
          <p className="mt-3 text-sm text-text-light">
            {result.stress_level === 'mild' && "Your responses suggest mild stress. You're experiencing normal life pressures."}
            {result.stress_level === 'moderate' && "Your responses suggest moderate stress. There are patterns worth addressing."}
            {result.stress_level === 'severe' && "Your responses suggest significant stress. These patterns are affecting multiple areas of your life."}
          </p>
        </div>

        {/* Summary */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">Summary</h2>
          <p className="text-text leading-relaxed text-base">{result.summary}</p>
        </section>

        {/* Cognitive Patterns */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4">Cognitive Patterns</h2>
          <div className="space-y-5">
            {result.cognitive_patterns.map((pattern, i) => (
              <div key={i} className="border-l-2 border-primary/30 pl-4">
                <div className="font-medium text-text mb-1">{pattern.distortion}</div>
                <div className="text-sm text-text-light mb-2 italic">"{pattern.evidence}"</div>
                <div className="text-sm text-primary font-medium">{pattern.reframe}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Triggers */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">Identified Triggers</h2>
          <div className="flex flex-wrap gap-2">
            {result.triggers.map((trigger, i) => (
              <span key={i} className="px-3 py-1.5 rounded-lg bg-bg border border-border text-sm text-text">
                {trigger}
              </span>
            ))}
          </div>
        </section>

        {/* Immediate Actions */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4 px-1">Immediate Actions</h2>
          <div className="space-y-4">
            {result.immediate_actions.map((action, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-5 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-text">{action.title}</h3>
                  <span className="text-xs text-text-light bg-bg px-2 py-1 rounded-md">{action.duration}</span>
                </div>
                <p className="text-sm text-text-light mb-3 leading-relaxed">{action.instructions}</p>
                <p className="text-xs text-primary font-medium">{action.why_it_helps}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 7-Day Plan */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4 px-1">Your 7-Day Plan</h2>
          <div className="space-y-4">
            {result.seven_day_plan.map((day, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                    {day.day}
                  </span>
                  <div>
                    <h3 className="font-semibold text-text text-sm">{day.focus}</h3>
                    <span className="text-xs text-text-light">{day.duration}</span>
                  </div>
                </div>
                <p className="text-sm text-text-light mb-2 leading-relaxed">{day.action}</p>
                <p className="text-xs text-text-light italic">💭 {day.reflection_prompt}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Seek Help */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-8">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">When to Seek Help</h2>
          <p className="text-sm text-text-light leading-relaxed mb-4">{result.seek_help.reason}</p>
          {result.seek_help.crisis_resources.length > 0 && (
            <div className="space-y-2">
              {result.seek_help.crisis_resources.map((r, i) => (
                <div key={i} className="text-sm font-medium text-severe bg-severe/5 px-3 py-2 rounded-lg">{r}</div>
              ))}
            </div>
          )}
        </section>

        {/* CTAs */}
        <div className="bg-white rounded-2xl border border-border p-6 text-center mb-8">
          <Bookmark className="w-6 h-6 text-primary mx-auto mb-3" />
          <h3 className="font-semibold text-text mb-1">Bookmark this page</h3>
          <p className="text-sm text-text-light mb-4">Save this URL to revisit your plan anytime.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-2.5 rounded-xl transition-colors inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Take another assessment
          </button>
        </div>
      </main>
    </div>
  )
}

export default SuccessPage
